import React, { useEffect, useMemo, useRef, useState } from "react";
import { getTodayEasternYMD } from "../../../../utils/dateUtils.ts";

const TOKEN_KEY = "scoutReportToken"; // stores ARRAY of {FBXID, ScoutReport, ReportDate}

const getTodayLocalYMD = () => getTodayEasternYMD();

export default function ReportList({
  height = 305,

  activePlayer,
  activeReports,

  totalCount = 0,
  privateCount = 0,

  currentScoutId = null,

  // token array + setter from DataLayer context
  reportData,
  updateReportData,

  // selection from parent
  selectedReportId,
  onReportChosen,

  // lock selection
  isLocked = false,
}) {
  const [hoveredReportId, setHoveredReportId] = useState(null);

  // for scroll-into-view
  const rowRefs = useRef({});
  const listRef = useRef(null);
  const stickyRef = useRef(null);

  const readLocalShared = () => {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);

      // migrate old single-object format -> array
      if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
        const migrated = [parsed];
        localStorage.setItem(TOKEN_KEY, JSON.stringify(migrated));
        return migrated;
      }

      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const writeLocalShared = (arr) => {
    try {
      localStorage.setItem(TOKEN_KEY, JSON.stringify(Array.isArray(arr) ? arr : []));
    } catch {
      // ignore
    }
  };

  // ONLY used on click (per noteList pattern)
  const upsertLocalForFBXID = (FBXID, ScoutReportId) => {
    if (!FBXID || ScoutReportId == null) return;

    const today = getTodayLocalYMD();
    const prev = readLocalShared();

    const idx = prev.findIndex((r) => String(r?.FBXID) === String(FBXID));
    let next;

    if (idx !== -1) {
      next = prev.map((r, i) =>
        i === idx ? { ...r, FBXID, ScoutReport: ScoutReportId, ReportDate: today } : r
      );
    } else {
      next = [...prev, { FBXID, ScoutReport: ScoutReportId, ReportDate: today }];
    }

    writeLocalShared(next);
    return next;
  };

  // robust private detection
  const isPrivateReport = (r) => {
    const raw = r?.isPrivate ?? r?.private ?? r?.Private ?? r?.PRIVATE;
    if (raw == null) return false;
    if (typeof raw === "boolean") return raw;
    const s = String(raw).trim().toLowerCase();
    return s === "1" || s === "y" || s === "yes" || s === "true";
  };

  // show EVERYTHING (including private not-mine), but only allow selecting what you can view
  const displayedReports = useMemo(() => {
    return Array.isArray(activeReports) ? activeReports : [];
  }, [activeReports]);

  const selectableReports = useMemo(() => {
    if (!Array.isArray(activeReports) || activeReports.length === 0) return [];

    return activeReports.filter((r) => {
      const priv = isPrivateReport(r);
      if (!priv) return true;

      const reportScoutNum = parseInt(r?.scoutnum, 10);
      const isMine = currentScoutId != null && reportScoutNum === currentScoutId;
      return isMine;
    });
  }, [activeReports, currentScoutId]);

  // Ensure selected report is always valid for the current player (auto-pick)
  useEffect(() => {
    const FBXID = activePlayer?.FBXID;
    if (!FBXID) {
      if (selectedReportId != null) onReportChosen(null);
      return;
    }

    if (!Array.isArray(selectableReports) || selectableReports.length === 0) {
      // you may still see rows (private not-mine), but nothing is selectable
      onReportChosen(null);
      return;
    }

    const currentOk =
      selectedReportId != null &&
      selectableReports.some((r) => String(r?.id) === String(selectedReportId));

    if (currentOk) return;

    // prefer DataLayer reportData if present, else localStorage
    const list = Array.isArray(reportData) ? reportData : [];
    const rowFromCtx = list.find((r) => String(r?.FBXID) === String(FBXID));
    const candidateId = rowFromCtx?.ScoutReport;

    if (candidateId != null && candidateId !== -1) {
      const match = selectableReports.find((r) => String(r?.id) === String(candidateId));
      if (match) {
        onReportChosen(match);
        return;
      }
    }

    const allLocal = readLocalShared();
    const rowFromLocal = allLocal.find((r) => String(r?.FBXID) === String(FBXID));
    const localId = rowFromLocal?.ScoutReport;

    if (localId != null && localId !== -1) {
      const match = selectableReports.find((r) => String(r?.id) === String(localId));
      if (match) {
        onReportChosen(match);
        return;
      }
    }

    // fallback: first selectable
    onReportChosen(selectableReports[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableReports, activePlayer?.FBXID, currentScoutId]);

  // Scroll selected row into view
  useEffect(() => {
    const id = selectedReportId;
    if (!id) return;

    const raf = requestAnimationFrame(() => {
      const el = rowRefs.current[String(id)];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [selectedReportId]);

  const handleSelectReport = (r) => {
    if (!r?.id) return;
    const FBXID = activePlayer?.FBXID;
    if (!FBXID) return;

    if (isLocked) return;

    // ignore private reports you can't view
    const priv = isPrivateReport(r);
    const reportScoutNum = parseInt(r?.scoutnum, 10);
    const isMine = currentScoutId != null && reportScoutNum === currentScoutId;
    if (priv && !isMine) return;

    if (String(selectedReportId) === String(r.id)) return;

    // update token array (ONLY on click)
    upsertLocalForFBXID(FBXID, r.id);

    // update DataLayer reportData (mirror localStorage)
    if (typeof updateReportData === "function") {
      const prev = Array.isArray(reportData) ? reportData : [];
      const today = getTodayLocalYMD();

      const idx = prev.findIndex((x) => String(x?.FBXID) === String(FBXID));
      let next;

      if (idx !== -1) {
        next = prev.map((x, i) =>
          i === idx ? { ...x, FBXID, ScoutReport: r.id, ReportDate: today } : x
        );
      } else {
        next = [...prev, { FBXID, ScoutReport: r.id, ReportDate: today }];
      }

      updateReportData(next);
    }

    onReportChosen(r);
  };

  return (
    <div
      className="border border-1 border-secondary rounded"
      style={{ height, overflowY: "auto" }}
      ref={listRef}
    >
      {/* ✅ STICKY HEADER WRAP (player summary + navy header stay pinned together) */}
      <div
        ref={stickyRef}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          background: "var(--bs-body-bg, #fff)",
          boxShadow: "0 1px 0 rgba(0,0,0,0.08)",
        }}
      >
        <div className="alert-secondary p-1 text-center fs-7 m-0">
          {activePlayer ? activePlayer.last : ""},{" "}
          {activePlayer ? activePlayer.first : ""},{" "}
          {activePlayer ? activePlayer.COL_POS1 : ""}{" "}
          <span className="text-muted">
            (Total: {totalCount} • Private: {privateCount})
          </span>
        </div>

        <div className="alert-navy p-1 text-center fs-7 m-0 fw-bold">
          Reports
        </div>
      </div>

      {!activePlayer?.FBXID ? (
        <div className="p-2 fs-7 text-muted">
          <em>Select a player below to load reports.</em>
        </div>
      ) : !activeReports ? (
        <div className="p-2 fs-7 alert-info fw-bold">Loading reports...</div>
      ) : displayedReports.length === 0 ? (
        <div className="p-2 fs-7 text-danger fw-bold">No reports found</div>
      ) : (
        displayedReports.map((r, i) => {
          const isSelected =
            selectedReportId != null && String(selectedReportId) === String(r?.id);
          const isHovered = String(hoveredReportId) === String(r?.id);
          const hoverClass = !isSelected && isHovered ? "alert-light" : "";

          const priv = isPrivateReport(r);
          const reportScoutNum = parseInt(r?.scoutnum, 10);
          const isMine = currentScoutId != null && reportScoutNum === currentScoutId;

          const isRestrictedPrivate = priv && !isMine;

          // If restricted, hide details and show "Private Report"
          const gradeText = isRestrictedPrivate ? "-" : (r.FINAL ?? r.final ?? "-");
          const calcText = isRestrictedPrivate ? "-" : (r.Final_Calculated ?? "-");
          const exposureText = isRestrictedPrivate ? "Private Report" : (r.exposure ?? "-");
          const scoutText = isRestrictedPrivate ? "-" : (r.SCOUT ?? r.scout ?? "-");

          const rowCursor = isLocked || isRestrictedPrivate ? "not-allowed" : "pointer";

          return (
            <div
              key={r?.id ?? i}
              ref={(el) => {
                const key = String(r?.id ?? "");
                if (!key) return;
                if (el) rowRefs.current[key] = el;
                else delete rowRefs.current[key];
              }}
              className={`d-flex fs-7 gap-2 px-2 py-1 align-items-center border-bottom
                ${isSelected ? "alert-warning" : ""}
                ${hoverClass}
                ${priv ? "text-danger" : ""}
                ${isRestrictedPrivate ? "fw-bold" : ""}`}
              style={{
                height: "35px",
                cursor: rowCursor,
                opacity: isLocked || isRestrictedPrivate ? 0.75 : 1,
              }}
              onMouseEnter={() => setHoveredReportId(r?.id)}
              onMouseLeave={() => setHoveredReportId(null)}
              onClick={() => {
                if (isRestrictedPrivate) return;
                handleSelectReport(r);
              }}
            >
              <div className="col-10">{r.REPORT_DATE ?? r.date ?? "-"}</div>
              <div className="flex-grow-1 text-truncate">{exposureText}</div>
              <div className="text-center col-3">{gradeText}</div>
              <div className="text-center col-3">{calcText}</div>
              <div className="col-7 text-truncate">{scoutText}</div>
            </div>
          );
        })
      )}
    </div>
  );
}
